import { createFileRoute } from "@tanstack/react-router";
import TitleBar from "@/components/layout/TitleBar";
import PageContent from "@/components/PageContent";
import { Select } from "@/components/ui/Select";

import { EvtccExam, evtccTestCategories as testCategories } from "@/data/evtccExams";
import { getDownloadUrl } from "@/lib/media";

export const Route = createFileRoute('/exams')({
  component: Exams,
})

const selectOptions = testCategories.flatMap((cat) =>
  cat.exams.map((exam) => ({
    value: exam.code,
    label: exam.code,
    subLabel: exam.title,
  }))
);

function ExamCard({ exam }: { exam: EvtccExam }) {
  return (
    <>
      <h3 id={exam.code}>{exam.code} &ndash; {exam.title}</h3>
      <p>{exam.description}</p>
      {exam.files.length > 0 && (
        <ul>
          {exam.files.map((file) => (
            <li key={file.url}>
              <a href={file.url}>{file.title}</a>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function Exams() {
  const jumpToTest = (code: string) => {
    document.getElementById(code)?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return (
    <>
      <TitleBar />
      <PageContent>
        <section>
          <h1>
            Exams &amp; Prep
          </h1>
          <p>
            Here you will find brief descriptions of all EVTCC tests, as well
            as our &ldquo;Reference Materials and Learning Objectives&rdquo;
            and &ldquo;Sample Questions&rdquo; documents for each test.
          </p>
          <h2>
            Quick Facts
          </h2>
          <ul>
            <li>Save 10% on registration costs by registering online.</li>
            <li>
              <a href="https://register.evtcc.org">Online registrations</a> are due 16 days prior to the test date.
            </li>
            <li>
              <a href={getDownloadUrl("EVTCC-Exam-Registration.pdf")}>Form registrations</a> must be <em>received</em> (by fax, mail, or email) 21 days prior to
              the test date.
            </li>
            <li>
              You must have your Confirmation/Admittance letter to gain
              admittance to your exam. Contact the EVTCC office if you have
              not received your Confirmation/Admittance letter by 16 days
              prior to the test date.
            </li>
            <li>A 70% score or better is required to pass each test.</li>
            <li>
              Results will be mailed within 4-6 weeks after the test date.
            </li>
            <li>Certifications are valid for 5 years.</li>
          </ul>
        </section>
        <h2 className="mt-4">
          Jump to an exam
        </h2>
        <Select
          options={selectOptions}
          onChange={jumpToTest}
          className="text-fluid-body"
          placeholder="Jump to an exam..."
        />

        {testCategories.map((testCategory) => {
          return (
            <section key={testCategory.title} className="mt-8">
              <h2>
                {testCategory.title}
              </h2>
              {testCategory.exams.map((exam) => <ExamCard key={exam.code} exam={exam} />)}
            </section>
          );
        })}
      </PageContent>
    </>
  );
}
